/**
 * Vendor Initialization Module
 *
 * Centralized initialization for all third-party vendors.
 * All functions are idempotent and non-essential network scripts are not loaded
 * until the relevant consent is explicitly granted.
 *
 * Vendors by category:
 * - Strictly Necessary: Sentry (error tracking, PII-scrubbed, for stability/security)
 * - Analytics: Google Analytics 4, DataFast
 * - Marketing: Google Ads (gtag)
 */

import {
  initializeGoogleTag,
  isGoogleAnalyticsConfigured,
  resetGoogleTagStateForTests,
} from './googleTag'
import {
  isSearchReplayDisabled,
  searchBreadcrumbAllowed,
  scrubSearchTelemetry,
} from '../searchPrivacy'
import { isWebMeasurementAllowed } from '../measurementPolicy'
import {
  initializeDataFast as initDataFast,
  cleanupDataFast,
  resetDataFastStateForTests,
} from './datafast'

// Track initialization state to ensure idempotency
let analyticsInitialization: Promise<boolean> | null = null
let marketingInitialized = false
let sentryInitialized = false
let gaInitialized = false
let gaReady = false

/**
 * Initialize strictly necessary vendors (Sentry for error tracking)
 * Called immediately on app load - no consent required
 */
export async function initStrictlyNecessaryVendors(): Promise<void> {
  await initSentry()
}

/**
 * Initialize all analytics vendors
 * Called when user grants analytics consent
 */
export function initAnalyticsVendors(): Promise<boolean> {
  if (!isWebMeasurementAllowed()) return Promise.resolve(false)
  if (analyticsInitialization) return analyticsInitialization

  // Initialize GA and DataFast in parallel (Sentry loaded separately)
  analyticsInitialization = Promise.all([initGoogleAnalytics(), initDataFast()]).then(
    ([googleAnalyticsReady]) => googleAnalyticsReady
  )

  return analyticsInitialization
}

/**
 * Initialize Google Analytics 4 via dynamic import
 */
async function initGoogleAnalytics(): Promise<boolean> {
  if (gaInitialized) return gaReady
  gaInitialized = true

  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID
  if (!measurementId) {
    console.warn('[Consent] GA4: VITE_GA_MEASUREMENT_ID not configured')
    return false
  }

  // Skip in development
  if (import.meta.env.DEV) {
    console.warn('[Consent] GA4: Skipped in development mode')
    return false
  }

  gaReady = isGoogleAnalyticsConfigured()
  return gaReady
}

/**
 * Initialize Sentry error tracking via dynamic import
 * Strictly necessary - initialized immediately for stability/security
 */
async function initSentry(): Promise<void> {
  if (sentryInitialized) return
  sentryInitialized = true

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    console.warn('[Consent] Sentry: VITE_SENTRY_DSN not configured')
    return
  }

  try {
    const Sentry = await import('@sentry/react')
    Sentry.init({
      dsn,
      environment: import.meta.env.VITE_ENVIRONMENT || 'development',
      integrations: [
        Sentry.browserTracingIntegration(),
        ...(!isSearchReplayDisabled()
          ? [
              Sentry.replayIntegration({
                // Enhanced privacy settings for strictly necessary use
                maskAllText: true,
                blockAllMedia: true,
              }),
            ]
          : []),
      ],
      // PII scrubbing configuration
      beforeBreadcrumb(breadcrumb, hint) {
        if (!searchBreadcrumbAllowed(breadcrumb.category, breadcrumb.data, hint?.event?.target))
          return null
        return scrubSearchTelemetry(breadcrumb)
      },
      beforeSendTransaction(event) {
        return scrubSearchTelemetry(event)
      },
      beforeSend(event) {
        if (document.querySelector('[data-private-search]')) return null
        // Strip email addresses from error messages
        if (event.message) {
          event.message = event.message.replace(
            /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
            '[EMAIL REDACTED]'
          )
        }
        // Strip potential PII from breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((bc) => ({
            ...bc,
            message: bc.message?.replace(
              /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
              '[EMAIL REDACTED]'
            ),
          }))
        }
        // Don't send in development
        if (import.meta.env.VITE_ENVIRONMENT === 'development') {
          console.warn('[Consent] Sentry would send:', event)
          return null
        }
        return scrubSearchTelemetry(event)
      },
      tracesSampleRate: import.meta.env.VITE_ENVIRONMENT === 'production' ? 0.1 : 1.0,
      // This installed Replay SDK cannot redact every navigation recording event.
      // Search rollout excludes Replay; error monitoring retains scrubbed URLs.
      replaysSessionSampleRate: isSearchReplayDisabled() ? 0 : 0.05,
      replaysOnErrorSampleRate: isSearchReplayDisabled() ? 0 : 1.0,
      ignoreErrors: ['ResizeObserver loop limit exceeded', 'Non-Error promise rejection captured'],
    })
  } catch (error) {
    console.error('[Consent] Sentry: Failed to initialize', error)
  }
}

/**
 * Initialize marketing vendors (Google Ads)
 * Called when user grants marketing consent
 */
export async function initMarketingVendors(): Promise<void> {
  if (!isWebMeasurementAllowed()) return
  if (marketingInitialized) return
  marketingInitialized = true

  if (typeof document === 'undefined' || typeof window === 'undefined') return

  // Skip in development
  if (import.meta.env.DEV) {
    console.warn('[Consent] Google Ads: Skipped in development mode')
    return
  }

  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID
  if (!googleAdsId) {
    console.warn('[Consent] Google Ads: VITE_GOOGLE_ADS_ID not configured')
    return
  }

  try {
    initializeGoogleTag()
  } catch (error) {
    console.error('[Consent] Google Ads: Failed to initialize', error)
  }
}

/**
 * Cookie names to delete when revoking analytics consent
 */
const ANALYTICS_COOKIES = [
  '_ga',
  '_gid',
  '_gat',
  '_gat_gtag',
  'datafast_visitor_id',
  'datafast_session_id',
]

/**
 * Cookie names to delete when revoking marketing consent
 */
const MARKETING_COOKIES = ['_gcl_au', '_gcl_aw', '_gac']

/**
 * Delete a cookie by setting it expired
 */
function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return

  // Try multiple path/domain combinations
  const paths = ['/', '']
  const hostname = window.location.hostname
  const labels = hostname.split('.')
  const parentDomains =
    labels.length > 2
      ? labels.slice(0, -1).map((_, index) => `.${labels.slice(index).join('.')}`)
      : []
  const domains = [hostname, `.${hostname}`, '', ...parentDomains]

  for (const path of paths) {
    for (const domain of domains) {
      let cookieStr = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`
      if (domain) {
        cookieStr += `; domain=${domain}`
      }
      document.cookie = cookieStr
    }
  }
}

/**
 * Clean up analytics cookies when consent is revoked
 */
export function cleanupAnalyticsCookies(): void {
  cleanupDataFast()
  deleteMatchingCookies(ANALYTICS_COOKIES, /^(?:_ga(?:_|$)|_gid$|_gat(?:_|$)|datafast_)/)
  analyticsInitialization = null
  gaInitialized = false
  gaReady = false

  try {
    window.localStorage.setItem('datafast_ignore', 'true')
    document.getElementById('datafast-script')?.remove()
    ;(window as unknown as { datafast?: (...args: unknown[]) => void }).datafast = () => {}
  } catch {
    // Ignore cleanup failures; cookie deletion above is the critical path.
  }
}

/**
 * Clean up marketing cookies when consent is revoked
 */
export function cleanupMarketingCookies(): void {
  deleteMatchingCookies(MARKETING_COOKIES, /^(?:_gcl_|_gac(?:_|$))/)
  marketingInitialized = false
}

function deleteMatchingCookies(known: string[], pattern: RegExp): void {
  const existing =
    typeof document === 'undefined'
      ? []
      : document.cookie
          .split(';')
          .map((cookie) => cookie.trim().split('=')[0])
          .filter((name) => pattern.test(name))
  new Set([...known, ...existing]).forEach(deleteCookie)
}

/**
 * Reset initialization flags (for testing or full reset)
 */
export function resetVendorState(): void {
  analyticsInitialization = null
  marketingInitialized = false
  sentryInitialized = false
  gaInitialized = false
  gaReady = false
  resetDataFastStateForTests()
  resetGoogleTagStateForTests()
}
